import React, { useEffect, useRef, useState } from "react";
import { COLORS, FONT_BODY, FONT_MONO } from "../theme.js";
import { inputStyle } from "./styles.js";
import { searchCatalogue } from "../domain/products.js";

/* Search the product catalogue by brand and model.

   This is the catalogue's only way into the app. The imported rows carry no
   barcode, so the scanner can never match one; brand and model are what the
   data actually holds, so typing is what has to reach it. */

const MIN_CHARS = 2;
/* Long enough that a typed word is one request rather than five, short enough
   that results feel like they are keeping up with the keyboard. */
const DEBOUNCE_MS = 250;

export default function CatalogueSearch({ category, onPick }) {
  const [query, setQuery] = useState("");
  const [state, setState] = useState({ status: "idle", results: [] });

  /* Responses can arrive out of order — a short query is quicker to answer
     than the longer one typed after it — so each request carries a token and
     only the newest one is allowed to write to the screen. Without this,
     results for "ai" can land after results for "air jordan" and replace them. */
  const latest = useRef(0);

  /* The picker is called from a click handler, so an effect that depended on it
     would re-run whenever the parent re-rendered and restart the search. */
  const pickRef = useRef(onPick);
  useEffect(() => { pickRef.current = onPick; });

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_CHARS) {
      latest.current++;              // cancel anything in flight
      setState({ status: "idle", results: [] });
      return;
    }

    const token = ++latest.current;
    setState((prev) => ({ ...prev, status: "searching" }));

    const timer = setTimeout(async () => {
      const { results, error } = await searchCatalogue(q);
      if (token !== latest.current) return;
      if (error) {
        setState({ status: "error", results: [] });
        return;
      }
      setState({ status: "done", results: rankByCategory(results, category) });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, category]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search the catalogue — brand or model"
        aria-label="Search the catalogue"
        autoComplete="off"
        style={inputStyle}
      />

      <Note>{noteFor(state, query)}</Note>

      {state.results.length > 0 && (
        <ul
          style={{
            listStyle: "none", margin: 0, padding: 0,
            maxHeight: 232, overflowY: "auto",
            border: `1px solid ${COLORS.lineFaint}`, borderRadius: 12,
            background: COLORS.blackCard,
          }}
        >
          {state.results.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                onClick={() => { setQuery(""); pickRef.current?.(product); }}
                style={{
                  width: "100%", textAlign: "left", cursor: "pointer",
                  background: "transparent", border: "none",
                  borderBottom: `1px solid ${COLORS.lineFaint}`,
                  padding: "10px 12px",
                  display: "flex", flexDirection: "column", gap: 3,
                }}
              >
                <span style={{ fontFamily: FONT_BODY, fontSize: 13.5, color: COLORS.cream }}>
                  {product.brand} {product.model}
                </span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: COLORS.creamFaint, letterSpacing: 0.3 }}>
                  {[product.colorway, product.category].filter(Boolean).join(" · ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Note({ children }) {
  if (!children) return null;
  return (
    <span style={{ fontSize: 11.5, color: COLORS.creamDim, fontFamily: FONT_BODY }}>{children}</span>
  );
}

/* Each of these says something different, and saying the wrong one costs
   someone their time: "nothing catalogued" sends them to type the details in,
   while "couldn't reach" tells them to try again. */
function noteFor(state, query) {
  if (state.status === "searching") return "Searching the catalogue…";
  if (state.status === "error") return "Couldn't reach the catalogue — enter the details manually.";
  if (state.status === "done" && state.results.length === 0) {
    return `Nothing catalogued for "${query.trim()}" — enter the details manually.`;
  }
  return "";
}

/* The catalogue spans every category, and its labels were written outside this
   repo, so they are matched loosely and only used to order results. Filtering
   on them in the query would mean an unfamiliar spelling returned nothing at
   all — worse than showing a handbag below the sneakers. */
function rankByCategory(results, category) {
  const want = String(category || "").trim().toLowerCase();
  if (!want) return results;
  return results
    .map((product, i) => ({ product, i, match: String(product.category || "").trim().toLowerCase() === want }))
    .sort((a, b) => (a.match === b.match ? a.i - b.i : a.match ? -1 : 1))
    .map((entry) => entry.product);
}
